import { usePrevious, useUpdateEffect } from 'ahooks';
import { Col, Row, Select, SelectProps, Space } from 'antd';
import { FC, memo } from 'react';

import { FormSelectNotFoundContent } from '@/common/components';
import { parseSelectData } from '@/common/utils';

import {
  ProxyServerAddressesConfigTag,
  ProxyServerAddressesHealthTag,
  ProxyServerConfigurationStatuses,
  ProxyServerHealthStatuses,
  useAutocompleteProxyServerAddressesMutation,
} from '@/modules/ProxyServers';

interface Props extends Omit<SelectProps, 'notFoundContent' | 'options'> {
  proxyServerId: string | undefined;
}

export const ProxyServersAddressesAutocomplete: FC<Props> = memo((props) => {
  const { proxyServerId, loading, value, disabled } = props;

  const previousProxyServerId = usePrevious(proxyServerId);
  const { data, isError, error, isLoading, isSuccess, mutate, reset } = useAutocompleteProxyServerAddressesMutation()();

  useUpdateEffect(() => {
    if (previousProxyServerId && proxyServerId !== previousProxyServerId) {
      props.onChange?.(undefined, []);
      reset();
    }

    if (proxyServerId) {
      mutate(proxyServerId);
    }
  }, [proxyServerId]);

  return (
    <Select
      {...props}
      allowClear
      notFoundContent={<FormSelectNotFoundContent description="Немає адрес проксі-сервера" isError={isError} error={error} />}
      placeholder="Виберіть адресу проксі-сервера"
      value={value}
      disabled={isLoading || !proxyServerId || disabled}
      loading={isLoading || loading}
      optionRender={({ value: v }) => {
        const option = data?.find((el) => el.id === v);
        if (!option) return null;
        return (
          <Row gutter={[16, 0]} justify="space-between" align="middle">
            <Col>
              <span>{option.ip}</span>
            </Col>
            <Col style={{ alignItems: 'center' }}>
              <Space size="small">
                <ProxyServerAddressesHealthTag healthStatus={option.healthStatus} />
                <ProxyServerAddressesConfigTag configurationStatus={option.configurationStatus} />
              </Space>
            </Col>
          </Row>
        );
      }}
      options={parseSelectData(data, {
        value: 'id',
        label: 'ip',
        disabled: ({ healthStatus, configurationStatus }) =>
          healthStatus !== ProxyServerHealthStatuses.Working ||
          configurationStatus !== ProxyServerConfigurationStatuses.Configured,
      })}
    />
  );
});
